import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { PaperPlaneTilt, FloppyDisk, Flask } from '@phosphor-icons/react';
import { plannerApi } from '../plannerApi';

const DOW = ['月曜', '火曜', '水曜', '木曜', '金曜', '土曜', '日曜'];
const FREQ = [['weekly', '毎週'], ['biweekly', '2週間ごと'], ['monthly', '毎月'], ['manual', '手動のみ']];

export default function DigestSettingsDialog({ open, onOpenChange, periodLabel, weekStart }) {
  const [s, setS] = useState(null);
  const [recipients, setRecipients] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [target, setTarget] = useState('global'); // 'global' | department_id
  const [q, setQ] = useState('');
  const [busy, setBusy] = useState(false);
  const [forbidden, setForbidden] = useState(false);
  const [confirmSend, setConfirmSend] = useState(false);

  useEffect(() => {
    if (!open) return;
    setConfirmSend(false);
    setTarget('global');
    Promise.all([plannerApi.digestSettings(), plannerApi.digestRecipients(), plannerApi.departments()])
      .then(([st, rc, dp]) => { setS({ dept_recipients: {}, ...st }); setRecipients(rc); setDepartments(dp); setForbidden(false); })
      .catch((e) => { if (e?.response?.status === 403) setForbidden(true); });
  }, [open]);

  const filtered = useMemo(() => {
    const n = q.trim().toLowerCase();
    return n ? recipients.filter((r) => `${r.name} ${r.email} ${r.department}`.toLowerCase().includes(n)) : recipients;
  }, [recipients, q]);

  const currentIds = useMemo(
    () => (target === 'global' ? (s?.recipient_ids || []) : (s?.dept_recipients?.[target] || [])),
    [s, target],
  );
  const selectedRecipients = useMemo(
    () => recipients.filter((r) => currentIds.includes(r.id)),
    [recipients, currentIds],
  );
  const deptName = useMemo(() => Object.fromEntries(departments.map((d) => [d.id, d.name])), [departments]);
  const configuredDepts = useMemo(
    () => Object.entries(s?.dept_recipients || {}).filter(([, ids]) => (ids || []).length > 0),
    [s],
  );
  const totalRecipients = (s?.recipient_ids || []).length
    + configuredDepts.reduce((a, [, ids]) => a + ids.length, 0);

  const toggle = (id) => setS((p) => {
    if (target === 'global') {
      return {
        ...p,
        recipient_ids: p.recipient_ids.includes(id)
          ? p.recipient_ids.filter((x) => x !== id)
          : [...p.recipient_ids, id],
      };
    }
    const cur = p.dept_recipients?.[target] || [];
    const next = cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id];
    return { ...p, dept_recipients: { ...p.dept_recipients, [target]: next } };
  });

  const save = async () => {
    setBusy(true);
    try {
      const saved = await plannerApi.saveDigestSettings(s);
      setS(saved);
      toast.success('ダイジェスト設定を保存しました。');
    } catch (e) {
      toast.error(e?.response?.data?.detail || '設定の保存に失敗しました');
    } finally { setBusy(false); }
  };

  const sendTest = async () => {
    setBusy(true);
    try {
      await plannerApi.sendDigest({ test: true, start: weekStart });
      toast.success('テストメールを送信しました。');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'メール送信に失敗しました。送信先と設定を確認してください。');
    } finally { setBusy(false); }
  };

  const sendNow = async () => {
    setBusy(true);
    try {
      const r = await plannerApi.sendDigest({ test: false, start: weekStart });
      toast.success(`ダイジェストを送信しました（${r.sent_to}名）。`);
      setConfirmSend(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'メール送信に失敗しました。送信先と設定を確認してください。');
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" aria-describedby={undefined} data-testid="digest-settings-dialog">
        <DialogHeader><DialogTitle>ダイジェスト設定</DialogTitle></DialogHeader>
        {forbidden ? (
          <p className="text-sm text-zinc-500 py-6 text-center">この設定を変更する権限がありません（管理者・マネージャのみ）。</p>
        ) : !s ? (
          <p className="text-sm text-zinc-400 py-6 text-center">読み込み中…</p>
        ) : (
          <div className="space-y-4">
            <div className="flex items-center justify-between rounded-lg border border-zinc-200 px-3 py-2.5">
              <div>
                <div className="text-sm font-semibold text-zinc-700">ダイジェストメールを有効にする</div>
                <div className="text-[11px] text-zinc-400">有効にすると設定した頻度で自動送信されます。</div>
              </div>
              <Switch checked={s.enabled} onCheckedChange={(v) => setS((p) => ({ ...p, enabled: v }))} data-testid="digest-enabled-switch" />
            </div>

            <div>
              <Label className="text-xs font-semibold text-zinc-600">送信先</Label>
              <p className="text-[11px] text-zinc-400 mt-0.5 mb-1.5">
                全社ダイジェストの送信先と、部署ごとの送信先（その部署のプロジェクトのみ含むダイジェストが届きます）を別々に設定できます。
              </p>
              <Select value={target} onValueChange={setTarget}>
                <SelectTrigger className="h-9" data-testid="digest-target-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="global">全社ダイジェスト（全部署）</SelectItem>
                  {departments.map((d) => (
                    <SelectItem key={d.id} value={d.id}>
                      部署: {d.name}{(s.dept_recipients?.[d.id] || []).length > 0 ? `（${s.dept_recipients[d.id].length}名）` : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <div className="text-[11px] text-zinc-500 mt-1.5">
                {target === 'global' ? '全社ダイジェスト' : `部署「${deptName[target] || ''}」`}の送信先（{currentIds.length}名選択中）
              </div>
              {selectedRecipients.length > 0 && (
                <div className="flex flex-wrap gap-1 mt-1" data-testid="digest-selected-recipients">
                  {selectedRecipients.map((r) => (
                    <span key={r.id} className="inline-flex items-center gap-1 rounded-full bg-indigo-50 text-indigo-700 border border-indigo-200 px-2 py-0.5 text-[11px]">
                      {r.name}
                      <button type="button" className="hover:text-rose-500" onClick={() => toggle(r.id)}>×</button>
                    </span>
                  ))}
                </div>
              )}
              <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="名前・メール・部署で検索…" className="h-9 mt-1.5" data-testid="digest-recipient-search" />
              <div className="mt-1.5 max-h-40 overflow-y-auto rounded-lg border border-zinc-100 divide-y divide-zinc-50">
                {filtered.length === 0 ? (
                  <p className="text-[11px] text-zinc-400 p-3">メールアドレス登録済みの候補がありません。</p>
                ) : filtered.map((r) => (
                  <label key={r.id} className="flex items-center gap-2 px-3 py-1.5 text-sm cursor-pointer hover:bg-zinc-50" data-testid={`digest-recipient-${r.id}`}>
                    <input type="checkbox" checked={currentIds.includes(r.id)} onChange={() => toggle(r.id)} className="accent-indigo-600" />
                    <span className="flex-1 min-w-0 truncate">{r.name}</span>
                    <span className="text-[10px] text-zinc-400 truncate max-w-40">{r.email}{r.department ? `・${r.department}` : ''}</span>
                  </label>
                ))}
              </div>
              {configuredDepts.length > 0 && (
                <div className="mt-2 rounded-lg bg-zinc-50 border border-zinc-100 px-3 py-2 text-[11px] text-zinc-600 space-y-0.5" data-testid="digest-dept-summary">
                  <div className="font-semibold text-zinc-500">部署別の設定済み送信先:</div>
                  {configuredDepts.map(([id, ids]) => (
                    <div key={id} className="flex items-center justify-between gap-2">
                      <span>{deptName[id] || id} — {ids.length}名</span>
                      <button type="button" className="text-zinc-400 hover:text-rose-500"
                        onClick={() => setS((p) => { const d = { ...p.dept_recipients }; delete d[id]; return { ...p, dept_recipients: d }; })}
                        data-testid={`digest-dept-clear-${id}`}>クリア</button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs font-semibold text-zinc-600">送信頻度</Label>
                <Select value={s.frequency} onValueChange={(v) => setS((p) => ({ ...p, frequency: v }))}>
                  <SelectTrigger className="h-10 mt-1" data-testid="digest-frequency-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{FREQ.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs font-semibold text-zinc-600">送信曜日</Label>
                <Select value={String(s.day_of_week)} onValueChange={(v) => setS((p) => ({ ...p, day_of_week: Number(v) }))}>
                  <SelectTrigger className="h-10 mt-1" data-testid="digest-dow-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{DOW.map((l, i) => <SelectItem key={l} value={String(i)}>{l}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs font-semibold text-zinc-600">送信時間</Label>
                <Select value={String(s.send_hour)} onValueChange={(v) => setS((p) => ({ ...p, send_hour: Number(v) }))}>
                  <SelectTrigger className="h-10 mt-1" data-testid="digest-hour-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{Array.from({ length: 24 }, (_, i) => <SelectItem key={i} value={String(i)}>{i}:00 以降</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs font-semibold text-zinc-600">ダイジェスト言語</Label>
                <Select value={s.language} onValueChange={(v) => setS((p) => ({ ...p, language: v }))}>
                  <SelectTrigger className="h-10 mt-1" data-testid="digest-lang-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ja">日本語</SelectItem>
                    <SelectItem value="en">English</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <p className="text-[11px] text-zinc-400">
              ファイル形式: メール本文はHTMLサマリーです。PDFはダイジェスト画面の「印刷 / PDF」からブラウザで保存できます。詳細ページはログインが必要です。
            </p>

            {confirmSend ? (
              <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 space-y-2" data-testid="digest-send-confirm">
                <p className="text-xs text-amber-800 font-semibold">以下の内容で送信します。よろしいですか？</p>
                <p className="text-[11px] text-amber-700">
                  対象期間: {periodLabel}<br />
                  全社ダイジェスト: {(s.recipient_ids || []).length}名<br />
                  {configuredDepts.map(([id, ids]) => (
                    <span key={id}>部署「{deptName[id] || id}」: {ids.length}名<br /></span>
                  ))}
                </p>
                <div className="flex gap-2">
                  <Button size="sm" className="bg-amber-600 hover:bg-amber-700" disabled={busy || totalRecipients === 0} onClick={sendNow} data-testid="digest-send-confirm-btn">送信する</Button>
                  <Button size="sm" variant="outline" onClick={() => setConfirmSend(false)}>キャンセル</Button>
                </div>
              </div>
            ) : (
              <div className="flex justify-end gap-2 flex-wrap pt-1">
                <Button variant="outline" disabled={busy} onClick={sendTest} data-testid="digest-test-btn">
                  <Flask size={14} className="mr-1" />テスト送信（自分宛）
                </Button>
                <Button variant="outline" disabled={busy || totalRecipients === 0} onClick={() => setConfirmSend(true)} data-testid="digest-send-now-btn">
                  <PaperPlaneTilt size={14} className="mr-1" />今すぐ送信
                </Button>
                <Button className="bg-teal-700 hover:bg-teal-800" disabled={busy} onClick={save} data-testid="digest-save-btn">
                  <FloppyDisk size={14} className="mr-1" />設定を保存
                </Button>
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
